import { Input, Text } from "@mantine/core";
import { IconRefresh } from "@tabler/icons-react";
import { type ChangeEvent, useMemo, useRef } from "react";
import { useController, useFormContext } from "react-hook-form";
import useSavedCursorPosition from "src/hooks/useSavedCursorPosition";
import { useDispatch } from "src/redux/hooks";
import { setUnitConverterModalData } from "src/redux/slices/generatorFeaturesSlice";

import useNMEAAutomated from "../hooks/useNMEAAutomated";
import { useLogbookRefsContext } from "../Logbook/LogbookRefsContext";
import { unitToName } from "./unitToName";

export interface ISelectOptionsData {
  value: string;
  label: string;
}

interface Props {
  converterName: string;
  value: any;
  selectOptionsData: ISelectOptionsData[];
  id: string;
  sectionIndex?: number;
  name?: string;
  source?: "auto" | "manual";
  rowIndex?: number;
}

const UnitConverter = ({
  converterName,
  selectOptionsData,
  value,
  id,
  sectionIndex = -1,
  source,
  name,
}: Props) => {
  const dispatch = useDispatch();
  const { ref: addRef, onKeyDown } = useLogbookRefsContext(id);
  const { control } = useFormContext();

  const parsedValue = useMemo(() => {
    if (typeof value === "object") {
      return {
        value: value?.value ?? "",
        unit: value?.unit ?? selectOptionsData[0].value,
      };
    }
    return {
      value,
      unit: selectOptionsData[0].value,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps kept as they were; changing them changes when this runs
  }, [value]);
  const { handleNMEAChange } = useNMEAAutomated({
    id,
    source,
    sectionIndex,
    defaultValue: parsedValue,
    name,
  });
  const {
    field: { onChange, ...field },
  } = useController({
    name: id,
    control,
    defaultValue: parsedValue,
  });

  const inputRef = useRef<HTMLInputElement | null>(null);

  const { handleFocus, handleBlur, setCursorPosition } = useSavedCursorPosition(
    id,
    inputRef,
  );
  const handleChangeWithRecording = (value: any) => {
    onChange(value);
    handleNMEAChange(value);
  };
  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCursorPosition();
    const inputValue = event.target.value;
    if (inputValue === "" || /^\d+(\.\d*)?$/.test(inputValue)) {
      const newValue = {
        value: inputValue ?? "",
        unit: field.value?.unit ?? selectOptionsData[0].value,
      };
      onChange(newValue);
    }
  };

  const handleInputBlur = (e: any) => {
    handleBlur(e);
    handleNMEAChange(field.value);
  };

  const handleOpen = () => {
    dispatch(
      setUnitConverterModalData({
        converterName,
        selectOptionsData,
        sectionIndex,
        onChange: handleChangeWithRecording,
        unit: field.value.unit,
        value: field.value.value,
      }),
    );
  };
  return (
    <Input
      miw={100}
      value={field.value?.value ?? ""}
      onChange={handleInputChange}
      rightSectionWidth="max-content"
      rightSectionProps={{
        style: {
          backgroundColor: "var(--input-bg)",
          padding: "0 5px",
        },
      }}
      rightSection={
        <>
          <Text size="sm" mr="xs" c="text">
            {unitToName[field.value?.unit ?? selectOptionsData[0].value]}
          </Text>
          <IconRefresh
            onClick={handleOpen}
            size="1rem"
            style={{
              display: "block",
              opacity: 0.5,
              cursor: "pointer",
            }}
            aria-label="RefreshIcon"
          />
        </>
      }
      ref={r => {
        inputRef.current = r;
        addRef(r);
      }}
      disabled={field.disabled}
      onKeyDown={onKeyDown}
      onFocus={handleFocus}
      onBlur={handleInputBlur}
    />
  );
};

export default UnitConverter;
